import { AbortRequestEventBus, MessageEventBus } from "../../../../event-bus";
import { isNumber, SimulationContext, SimulationControl, Slot } from "../simulation-control";
import { TimerService } from "./timer/TimerService";
import { get } from "lodash-es";

import { ErrorHandler } from "../../error-handler";
import RappidContext from "../../../rappid/rappid-context";
import { AxiosError, AxiosResponse } from "axios";
import { getStatechart } from "../../model-helper";
import { DirtyState } from "../../dirty-state";
import { curriedGet, curriedPost } from "../../abort-controller";
import { waitUntilCleanState } from "../../storage/utils";
import { Highlighter } from "../../highlighter";
import { HttpHeaders } from "../../../../../utils/http-utils";

class TraceObserver {
  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private TraceEventType: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private Statemachine: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private hashes: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private callback: any
  ) {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  next(traceEvent: any) {
    switch (traceEvent.getType()) {
      case this.TraceEventType.STATE_ENTERED:
        this.notifyStateEntered(traceEvent.getState());
        break;
      case this.TraceEventType.STATE_EXITED:
        this.notifyStateExited(traceEvent.getState());
        break;
      case this.TraceEventType.STATE_TRANSITION:
        this.notifyReactionFired(traceEvent.getState(), traceEvent.getValue());
        break;
      case this.TraceEventType.VARIABLE_SET:
        this.notifyValueChanged(traceEvent.getFeature(), traceEvent.getValue());
        break;
    }
  }
  /* eslint-disable @typescript-eslint/no-explicit-any*/
  notifyStateEntered(state: any): void {
    const stateName = this.Statemachine.State[state];
    const stateHash = this.hashes[stateName];
    const data = {
      stateId: stateHash,
      stateName: stateName,
    };
    this.callback.onStateEnteredCB(data);
  }
  notifyStateExited(state: any): void {
    const stateName = this.Statemachine.State[state];
    const stateHash = this.hashes[stateName];
    const data = {
      stateId: stateHash,
      stateName: stateName,
    };
    this.callback.onStateExitedCB(data);
  }
  notifyReactionFired(state: any, trans: any): void {
    const stateName = this.Statemachine.State[state];
    const stateHash = this.hashes[stateName];
    this.callback.onReactionFiredCB(stateHash, trans);
  }
  notifyValueChanged(feature: any, value: any): void {
    const varName = this.Statemachine.Feature[feature];
    this.callback.onValueChangedCB(varName, value, "featureId");
  }
}

export type SimulationType = "store" | "player" | "vscode";

export class CodeSimulationService implements SimulationControl {
  private readonly config: {
    baseURL: string;
    headers: HttpHeaders;
    params: { type: SimulationType; cleanUp: boolean };
  };

  constructor(
    private rappidContext: RappidContext,
    private baseURL: string,
    private urlExtension: (id: string) => string = (id: string) => {
      return `/api/models/${id}/simulation`;
    },
    private type: SimulationType = "store",
    private waitForDirtyState = true,
    private dirtyState?: DirtyState,
    cleanUp = false
  ) {
    this.config = {
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
      params: { type: this.type, cleanUp: cleanUp },
    };
  }

  private machine: any; // Statemachine["__class"] = "sct.yakindu.io.Statemachine";
  private machineObservable: any; // Observable["__class"] = "com.yakindu.core.rx.Observable";
  private traceObserver: TraceObserver;
  private loadedResponse: AxiosResponse;
  private context: SimulationContext;
  private simulationId: string;
  private cycleInterval: NodeJS.Timeout;
  private loadingTimeout: NodeJS.Timeout;
  private errorHandler = new ErrorHandler("Simulation");
  private model: string;
  private loadingAborted = false;
  private isRunning = false;
  private loadingLoopDelay = 0;
  private err: (err: any) => void;

  // used for CREATE VSCode Extension
  async postModelForSimulation(jsonModel: string): Promise<string> {
    const requestPromise = curriedPost("/api/simulation", this.config, false, jsonModel);

    return await requestPromise
      .then((res) => {
        const newModelId: string = res.data;
        return newModelId;
      })

      .catch((e: string | AxiosError) => {
        if (typeof e === "string") {
          throw e;
        }
        throw this.errorHandler.handle(e, "Simulation", "Unknown Error", true);
      });
  }

  async load(id: string, err?: (err: any) => void): Promise<AxiosError | null | AxiosResponse> {
    if (id) this.simulationId = id;
    if (err) this.err = err;

    this.loadingAborted = false;
    const stopAbortHandler = () => {
      this.loadingAborted = true;
      AbortRequestEventBus.off("abort", stopAbortHandler);
    };
    AbortRequestEventBus.on("abort", stopAbortHandler);

    MessageEventBus.emit("information", {
      text: "Loading simulation...",
      id: "simulation-service-code",
      showProgress: true,
      timeout: -1,
    });

    if (this.waitForDirtyState && this.dirtyState?.isDirty())
      await waitUntilCleanState(this.dirtyState).catch(() => {
        throw this.errorHandler.handle(
          undefined,
          "simulation-service-code",
          "Simulation cannot be loaded. As model saving timed out."
        );
      });

    AbortRequestEventBus.off("abort", stopAbortHandler);

    return new Promise(
      (resolve: (value: AxiosError | null | AxiosResponse) => void, reject: (reason?: any) => void) => {
        // use recursive setTimeout with loadingLoop instead of setInterval to make sure, that next function call is only started after last one was finished
        this.startLoadingLoop(resolve, reject);

        this.loadingTimeout = setTimeout(
          () => {
            reject(null);
            this.handleLoadingAndSetupError(null);
          },
          1000 * 60 * 3
        );
      }
    );
  }

  private startLoadingLoop(
    resolve: (value: AxiosError | null | AxiosResponse) => void,
    reject: (reason?: any) => void
  ) {
    // start immediatly with the first request
    this.loadingLoopDelay = 0;
    this.loadingLoop(resolve, reject);
  }

  private loadingLoop(resolve: (value: AxiosError | null | AxiosResponse) => void, reject: (reason?: any) => void) {
    if (!this.loadingAborted) {
      setTimeout(async () => {
        const waitForLoadRequestPromise = curriedGet(this.urlExtension(this.simulationId), this.config);

        await waitForLoadRequestPromise
          .then(async (response) => {
            if (response.status === 201) {
              this.loadedResponse = response;
              this.cleanUpLoadingTimeouts();
              resolve(response);
              return;
            }
            // start with the next request only after 1s
            this.loadingLoopDelay = 1000;
            this.loadingLoop(resolve, reject);
          })
          .catch((e: AxiosError) => {
            reject(e);
            this.handleLoadingAndSetupError(e);
          });
      }, this.loadingLoopDelay);
    }
  }

  setup(setupCallback?: () => void) {
    if (this.loadedResponse?.status === 201 && !this.loadingAborted) {
      const code = this.loadedResponse.data.code;
      const hashes = JSON.parse(this.loadedResponse.data.hashMap);
      const context = JSON.parse(this.loadedResponse.data.context);

      this.model = this.loadedResponse.data.model;

      try {
        // https://gitlab.com/itemis/solutions/crt/create-web-frontend/-/issues/1073
        // nosemgrep: eslint.detect-eval-with-expression
        const { Statemachine, TraceEventType } = eval(
          `"use strict";
            ${code}`
        );

        this.machine = new Statemachine();

        this.machineObservable = this.machine.getTrace();

        this.traceObserver = new TraceObserver(TraceEventType, Statemachine, hashes, this);

        this.machineObservable.subscribe(this.traceObserver);

        this.context = new SimulationContext();
        this.context.elements = context.elements;

        this.setupOperationCallbacks();

        this.setupTimerService();
        this.setupCycleRunner();

        // turn off loading snackbar again
        MessageEventBus.emit("information", {
          text: "Loading simulation...",
          id: "simulation-service-code",
          timeout: 0.1,
        });

        if (setupCallback) setupCallback();
      } catch (e) {
        this.handleLoadingAndSetupError(e);
      }
    }
  }

  enter(enterCallback?: (_ctx: SimulationContext) => void) {
    if (!this.loadingAborted) {
      if (!this.machine) {
        throw this.errorHandler.handle(
          undefined,
          "simulation-service-code",
          "Simulation cannot be started as it isn't setup properly."
        );
      }
      if (!this.isRunning) {
        if (enterCallback) enterCallback(this.context);
        this.machine.enter();

        this.isRunning = true;

        MessageEventBus.emit("success", {
          text: "Simulation started.",
          id: "simulation-service-code",
        });
      }
    }
  }

  async exit(exitCallback?: () => void) {
    if (this.isRunning) {
      if (this.waitForDirtyState && this.dirtyState?.isDirty())
        await waitUntilCleanState(this.dirtyState).catch(() => {
          throw this.errorHandler.handle(
            undefined,
            "simulation-service-code",
            "Simulation cannot be loaded. As model saving timed out."
          );
        });

      if (!this.machine) {
        throw this.errorHandler.handle(
          undefined,
          "simulation-service-code",
          "Simulation cannot be stopped as it isn't setup properly."
        );
      }

      this.machine.exit();
      this.isRunning = false;

      if (exitCallback) exitCallback();

      MessageEventBus.emit("information", {
        text: "Simulation stopped.",
        id: "simulation-service-code",
      });
    }
  }

  tearDown() {
    this.abort();
    this.machineObservable?.unsubscribe(this.traceObserver);

    this.machine = undefined;
  }

  private abort() {
    AbortRequestEventBus.emit("abort");
    this.cleanUpLoadingTimeouts();
    if (this.cycleInterval) clearInterval(this.cycleInterval);

    MessageEventBus.emit("information", {
      text: "Simulation stopped.",
      id: "simulation-service-code",
    });
  }

  private handleLoadingAndSetupError(e: AxiosError | any) {
    this.abort();
    if (this.err) this.err(e);
    if (e.response?.status === 500) {
      const msg =
        this.type === "store" || this.type === "vscode"
          ? "Model contains errors or is empty so that the simulation cannot be loaded."
          : "The requested player is not available.";
      MessageEventBus.emit("error", {
        id: "simulation-service-code",
        text: msg,
      });
      throw msg;
    } else {
      throw this.errorHandler.handle(
        e,
        "simulation-service-code",
        "Simulation cannot be loaded.",
        true,
        "Loading of Simulation got manually aborted."
      );
    }
  }

  private cleanUpLoadingTimeouts() {
    if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
  }

  private setupCycleRunner() {
    const sc = getStatechart(this.rappidContext.paper.model);
    if (sc) {
      const cyclePeriod = sc.getCyclePeriod();
      if (cyclePeriod > -1) {
        this.cycleInterval = setInterval(() => this.machine.runCycle(), cyclePeriod);
      }
    }
  }

  private setupTimerService() {
    if (this.machine["setTimerService"]) {
      this.machine["setTimerService"](new TimerService());
    }
  }

  private setupOperationCallbacks() {
    const operationMap: Map<Slot, Array<Slot>> = new Map();
    this.context.elements
      .filter((e) => e.kind === "composite")
      .forEach((scope: Slot) => {
        scope.elements
          .filter((e: Slot) => e.kind === "operation")
          .forEach((o) => {
            let ops = operationMap.get(scope);
            if (!ops) ops = [];
            operationMap.set(scope, ops.concat([o]));
          });
      });

    Array.from(operationMap.entries()).forEach((entry) => {
      const key = entry[0];
      const values = entry[1];
      const cb = {};
      values.forEach(
        (op) =>
          // https://gitlab.com/itemis/solutions/crt/create-web-frontend/-/issues/1012
          (cb[this.firstLower(op.name)] = () => {
            return op.value;
          })
      );

      if (key.name === "internal") {
        this.machine["setInternalOperationCallback"].bind(this.machine)(cb);
      } else if (key.name === "default") {
        this.machine["setOperationCallback"].bind(this.machine)(cb);
      } else {
        const iface = this.machine[key.name].bind(this.machine)();
        iface["setOperationCallback"].bind(iface)(cb);
      }
    });
  }

  private firstLower(str: string): string {
    if (!str) return str;
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  raiseEvent(fqName: string, value?: any) {
    const slot = this.context.findByFqn(fqName);
    const timeEvents = this.context.elements.find((e) => e.name === "time events");
    if (timeEvents) {
      const timeEvId = timeEvents.elements.indexOf(slot);
      if (timeEvId >= 0) {
        this.callFeature(slot.name, timeEvId, () => "raiseTimeEvent");
        return;
      }
    }
    let newValue = undefined;
    if (value !== undefined) newValue = isNumber(slot) ? +value : value;
    this.callFeature(slot.fqName, newValue, (s) => "raise" + s.charAt(0).toUpperCase() + s.slice(1));
  }

  changeValue(fqName: string, newValue: any): void {
    const slot = this.context.findByFqn(fqName);
    const val = isNumber(slot) ? +newValue : newValue;
    const timeEvents = this.context.elements.find((e) => e.name === "time events");
    if (timeEvents) {
      const timeEvId = timeEvents.elements.indexOf(slot);
      if (timeEvId >= 0) {
        this.callFeature(slot.name, timeEvId, () => "setTimeEvent");
        return;
      }
    }
    this.callFeature(slot.fqName, val, (s) => "set" + s.charAt(0).toUpperCase() + s.slice(1));
  }

  callFeature(fqn: string, value: any, featureName: (s: string) => string) {
    let context = this.machine;
    let name = fqn;
    const segments = fqn.split(".");
    if (segments.length > 1) {
      const path = segments.slice(0, -1);
      path[0] = path[0].charAt(0).toLowerCase() + path[0].slice(1);
      name = segments.slice(-1)[0];
      context = get(this.machine, path).bind(this.machine)();
    }
    const feature = featureName(name);
    if (typeof context[feature] === "function")
      if (value !== undefined) context[feature].bind(context)(value);
      else context[feature].bind(context)();
    else if (value !== undefined) context[feature] = value;
  }

  private onStateEnteredCB: (data: { stateId: string; stateName: string }) => void;

  addOnStateEntered(callback: (data: { stateId: string; stateName: string }) => void): SimulationControl {
    this.onStateEnteredCB = callback;
    return this;
  }

  private onStateExitedCB: (data: { stateId: string; stateName: string }) => void;

  addOnStateExited(callback: (data: { stateId: string; stateName: string }) => void): SimulationControl {
    this.onStateExitedCB = callback;
    return this;
  }

  private onReactionFiredCB: (stateId: string, transId: string) => void;
  addOnReactionFired(callback: (stateId: string, transId: string) => void): SimulationControl {
    this.onReactionFiredCB = callback;
    return this;
  }

  private onValueChangedCB: (
    varId: string,

    value: any,
    varIdProp?: string
  ) => void;

  addOnValueChanged(callback: (varId: string, value: any, varIdProp: any) => void): SimulationControl {
    this.onValueChangedCB = callback;
    return this;
  }

  getModel() {
    return this.model;
  }

  getContext() {
    return this.context;
  }

  getIsRunning(): boolean {
    return this.isRunning;
  }
}
